import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { Question } from 'app/shared/services/app/content/models';
import { LoaderButton } from 'app/shared/components/loader-button/loader-button.component';

@Component({
  selector: 'tog-question-builder',
  templateUrl: './question-builder.component.html',
  styleUrls: ['./question-builder.component.scss']
})
export class QuestionBuilderComponent implements OnInit {
  question: Question = new Question();

  @Input() questionToEdit: Question;
  @Input() allowFeedback: boolean = true;

  @Output() onQuestionBuilderCancel: EventEmitter<any> = new EventEmitter<any>();
  @Output() onCreateQuestion: EventEmitter<Question> = new EventEmitter<Question>();
  @Output() onEditQuestion: EventEmitter<Question> = new EventEmitter<Question>();
  @Output() onDidNotConfirmCancelEditing: EventEmitter<any> = new EventEmitter<any>();

  // For building a question
  activeSection: string = 'prompt';
  multipleChoiceAnswers: any[] = [];
  answerLetters: string[] = ['A', 'B', 'C', 'D'];
  feedbackType: string = '';

  @ViewChild('confirmCancelQuestionButton') confirmCancelQuestionButton: ElementRef;
  @ViewChild('confirmSaveQuestionButton') confirmSaveQuestionButton: LoaderButton;

  constructor() { }

  // If we are editing a question, put its content in our temp question and
  // update the form
  ngOnInit() {
    if (this.questionToEdit) {
      this.question.updateWithJson(this.questionToEdit.toJson());
      if (this.questionToEdit.type === 'multiple-choice') {
        this.multipleChoiceAnswers = this.questionToEdit.answers.map(answer => {
          return { answerText: answer };
        });
      }
      if (this.questionToEdit.alwaysFeedback.length) {
        this.feedbackType = 'always';
      } else if (this.questionToEdit.correctFeedback || this.questionToEdit.incorrectFeedback) {
        this.feedbackType = 'correct-incorrect';
      }
    }
  }

  // Update the multipleChoiceAnswers array with 4 arbitrary objects
  selectMultipleChoice() {
    if (this.question.type !== 'multiple-choice') {
      this.question.type = 'multiple-choice';
      this.multipleChoiceAnswers = [];
      this.question.correctAnswer = -1;
      if (this.multipleChoiceAnswers.length === 0) {
        for (let i = 0; i < 4; i++) {
          this.multipleChoiceAnswers.push({ answerText: '' });
        }
      }
    }
  }

  selectTrueFalse() {
    if (this.question.type !== 'true-false') {
      this.question.correctAnswer = -1;
      this.question.type = 'true-false';
    }
  }

  onFeedbackSelected(type: string) {
    this.question.alwaysFeedback = '';
    this.feedbackType = type;
  }

  saveQuestion() {
    if (this.isQuestionFormComplete()) {
      // Put the answers in the question model
      if (this.question.type === 'multiple-choice') {
        this.question.answers = this.multipleChoiceAnswers.map(answer => answer.answerText);
      }

      // If we're editing, update the inherited question with its new details
      if (this.questionToEdit) {
        this.questionToEdit.updateWithJson(this.question.toJson());
        if (this.onEditQuestion) this.onEditQuestion.emit(null);
      } else { // Otherwise send the new question to the parent
        if (this.onCreateQuestion) this.onCreateQuestion.emit(this.question);
      }

      this.confirmSaveQuestionButton.setConfirmed();
    }
  }

  // Is there a prompt? Is there a type? Is a correct answer selected?
  // Is feedback enabled and we have feedback set?
  // Are all of the multiple choice answers filled in?
  isQuestionFormComplete() {
    if (!this.question.prompt.trim() || !this.question.type || this.question.correctAnswer === -1) return false;
    if (this.allowFeedback) {
      if (this.feedbackType === 'correct-incorrect' && (!this.question.correctFeedback.trim() || !this.question.incorrectFeedback.trim())) return false;
      if (this.feedbackType === 'always' && !this.question.alwaysFeedback.trim()) return false;
    }
    if (this.question.type === 'multiple-choice' &&
        this.multipleChoiceAnswers.filter(answer => !answer.answerText.trim()).length > 0) return false;
    return true;
  }

  // If editing a question, check to see if it has been changed and present popup
  // If creating a question, we only need to check if prompt or type has been edited
  cancelAddingQuestion() {
    if (this.questionToEdit) {
      let previousJson = this.questionToEdit.toJson();
      let newJson = this.question.toJson();
      if (JSON.stringify(previousJson) !== JSON.stringify(newJson)) {
        this.confirmCancelQuestionButton.nativeElement.click();
      } else {
        this.confirmCancel();
      }
    } else {
      if (this.question.type || this.question.prompt) {
        this.confirmCancelQuestionButton.nativeElement.click();
      } else {
        this.confirmCancel();
      }
    }
  }

  // An evit emitted when the user clicks "Cancel" on the confirmation modal
  didNotConfirmCancelEditing() {
    this.onDidNotConfirmCancelEditing.emit();
  }

  confirmCancel() {
    if (this.onQuestionBuilderCancel) this.onQuestionBuilderCancel.emit(null);
  }
}



// WEBPACK FOOTER //
// /app/src/app/shared/components/question-builder/question-builder.component.ts